<template>
  <b-row>
    <!-- The Label Container -->
    <b-col
      id="printable-section"
      md="6"
      cols="12"
      class="print-wrapper"
    >

      <b-card>
        <div class="b-card-container">
          <!-- Header: Logo & QR -->
          <div class="header-section">
            <div class="company-info">
              <!-- Logo SVG -->
              <div class="logo-box">
                <b-img
                  class="dn-brand-logo"
                  :src="courier.company_logo ? `${bucketUrl}${courier.company_logo}` : defaultCompanyLogo"
                  alt="logo"
                />
              </div>
              <div class="company-text">
                <h1 class="dn-business-name">
                  {{ courier.company_name }}
                </h1>
                <p class="address">
                  {{ courier.company_address }}
                </p>
                <p class="contact">
                  Contact: {{ courier.company_phone }}
                </p>
              </div>
            </div>

            <div class="qr-wrapper">
              <!-- Using your custom QR Component -->
              <QRcode
                :id="bagData.display_name"
                width="90px"
                height="90px"
              />
              <span class="scan-label">SCAN CODE</span>
            </div>
          </div>

          <hr class="divider-thick">

          <!-- Info Grid -->
          <div class="info-grid">

            <!-- Shuttle Code -->
            <div class="info-box">
              <div class="box-label">
                <feather-icon
                  class="icon"
                  icon="HashIcon"
                  size="12"
                />
                <span>Shuttle Code</span>
              </div>
              <div class="box-value">
                {{ shuttleCode(bagData.branch.key) }}
              </div>
            </div>

            <!-- Shuttle Name -->
            <div class="info-box">
              <div class="box-label">
                <feather-icon
                  class="icon"
                  icon="PackageIcon"
                  size="12"
                />
                <span>Shuttle Name</span>
              </div>
              <div class="box-value">
                {{ bagData.display_name }}
              </div>
            </div>

            <!-- Branch Code -->
            <div class="info-box">
              <div class="box-label">
                <feather-icon
                  class="icon"
                  icon="MapPinIcon"
                  size="12"
                />
                <span>Branch Code</span>
              </div>
              <div class="box-value">
                {{ bagData.branch.key }}
              </div>
            </div>

            <!-- Branch Name -->
            <div class="info-box">
              <div class="box-label">
                <feather-icon
                  class="icon"
                  icon="MapPinIcon"
                  size="12"
                />
                <span>Branch Name</span>
              </div>
              <div class="box-value">
                {{ bagData.branch.name }}
              </div>
            </div>

            <!-- Date & Time (Full Width) -->
            <div class="info-box full-width">
              <div class="box-label">
                <feather-icon
                  class="icon"
                  icon="CalendarIcon"
                  size="12"
                />
                <span>Date & Time</span>
              </div>
              <div class="box-value">
                {{ dispatchedDateTime }}
              </div>
            </div>
          </div>

          <hr class="divider-thick">

          <!-- Footer Info -->
          <div class="footer-section">
            <b-col class="footer-item">
              <div class="box-label">
                <feather-icon
                  class="icon"
                  icon="UserIcon"
                  size="12"
                />
                <span>Dispatched By</span>
              </div>
              <div class="footer-value">
                {{ `${currentUser.first_name || ''} ${currentUser.last_name || ''}` }}
              </div>
            </b-col>
            <b-col class="footer-item">
              <div class="box-label">
                <feather-icon
                  class="icon"
                  icon="PackageIcon"
                  size="12"
                />
                <span>Quantity</span>
              </div>
              <div class="footer-value">
                {{ bagData.count }} Packs
              </div>
            </b-col>
          </div>

          <hr class="divider-thin">

          <!-- Barcode Section -->
          <div class="barcode-section">
            <p class="barcode-title">
              TRACKING BARCODE
            </p>
            <div class="barcode-wrapper">
              <!-- Using your custom Barcode Component -->
              <Barcode
                :id="bagData.display_name"
                width="280px"
                height="50px"
              />
            </div>
          </div>

          <hr class="divider-last">

          <div class="final-wrapper">
            <p class="text">
              This document is computer generated and requires no signature
            </p>
            <p class="company">
              {{ courier.company_name }} | Contact: {{ courier.company_phone }}
            </p>
          </div>

        </div>
      </b-card>

    </b-col>

    <b-col
      md="6"
      cols="12"
      class="no-print"
    >
      <b-card>
        <!-- Button: Print -->
        <b-button
          v-ripple.400="'rgba(186, 191, 199, 0.15)'"
          variant="outline-warning"
          class="mb-75"
          block
          @click="triggerPrint"
        >
          Print
        </b-button>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BButton, BCard, BCol, BImg, BRow,
} from 'bootstrap-vue'
import { bucketUrl, defaultCompanyLogo } from '@/constants/config'
import Barcode from '@/components/print/Barcode.vue'
import QRcode from '@/components/print/QRcode.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const sortingRepository = RepositoryFactory.get('sorting')

export default {
  name: 'PrintBag',
  components: {
    BButton,
    BCard,
    BCol,
    BImg,
    BRow,
    Barcode,
    QRcode,
  },
  data() {
    return {
      courier: {},
      currentUser: {},
      bagData: {},
      bucketUrl,
      defaultCompanyLogo,
      dispatchedDateTime: '',
    }
  },
  mounted() {
    this.courier = JSON.parse(localStorage.getItem('businessSettings'))
    this.currentUser = JSON.parse(localStorage.getItem('current_user'))
    this.getBagDetails()
    this.formattedDate()
  },
  methods: {
    async getBagDetails() {
      try {
        const { data } = (await sortingRepository.getBagDetails(this.$route.params.bagId))
        this.bagData = data.data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    shuttleCode(branchName) {
      return branchName.split('_')[1] || ''
    },
    formattedDate() {
      const date = new Date()
      // Format: Nov 19, 2025, 4:20 PM
      this.dispatchedDateTime = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      })
    },
    triggerPrint() {
      window.print()
    },
  },
}
</script>

<style scoped>
/* GENERAL LAYOUT */
.print-wrapper {
  font-family: 'Arial', sans-serif;
  color: black;
  margin: 0 auto;
  box-sizing: border-box;
  border-radius: 0.428rem;
}

.action-bar {
  width: 100%;
  max-width: 800px;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.btn-print {
  padding: 10px 30px;
  font-weight: bold;
}

.b-card-container {
  border: 2px solid #000;
  box-sizing: border-box;
  height: 100%;        /* Fill the container */
  padding: 10px 0 0 0;
}

/* HEADER */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 10px;
}

.company-info {
  display: flex;
  flex-direction: column;
  align-items: left;
  gap: 5px;
}

.dn-brand-logo {
  display: block;
  width: 100px;
  height: 100px;
  margin: 5px 0 5px 0;
}

.company-text {
  padding-top: 5px;
  padding-bottom: 5px;
}

.company-text h1 {
  font-size: 14px;
  font-weight: 800;
  margin: 0;
  line-height: 1;
  text-transform: uppercase;
  color: black;
}

.company-text .address {
  line-height: 1;
  margin-bottom: 0;
  margin-top: 5px;
  font-size: 14px;
  font-weight: 600;
  color: #444;
}

.company-text .contact {
  line-height: 1;
  margin-bottom: 0;
  margin-top: 5px;
  font-size: 12px;
  font-weight: 600;
}

.qr-wrapper {
  display: flex;
  flex-direction: column;
  align-items: right;
}

.scan-label {
  font-size: 9px;
  font-weight: 500;
  text-align: right;
  padding-right: 8px;
}

/* DIVIDERS */
.divider-thick {
  margin: 15px 0 !important;
  border: 0;
  border-top: 2px solid black;
}

.divider-thin {
  border: 0;
  border-top: 1px solid #ccc;
  margin: 15px 10px;
}

.divider-last {
  border: 0;
  border-top: 1px solid #ccc;
  margin: 0;
}

/* INFO GRID */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  padding: 10px;
}

.info-box {
  border: 2px solid #000;
  background: white;
  padding: 5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Shadow effect from image */
  box-shadow: 4px 4px 0px rgba(0,0,0,0.1);
}

.full-width {
  grid-column: span 2;
}

.box-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #444;
}

.box-label .icon {
  width: 14px;
  height: 14px;
  color: #000;
}

.box-value {
  font-size: 14px;
  font-weight: 800;
  color: #000;
  padding-top: 5px;
}

/* FOOTER */
.footer-section {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  padding: 0 10px;
}

.footer-item {
  display: flex;
  flex-direction: column;
  padding-left: 0 !important;
}

.footer-item.align-right {
  align-items: flex-end;
}

.footer-value {
  font-size: 14px;
  font-weight: 700;
  margin-top: 5px;
}

.align-right .footer-value {
  padding-left: 0;
}

/* BARCODE */
.barcode-section {
  text-align: center;
  margin-top: 10px;
  padding: 10px;
}

.barcode-title {
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  text-align: left;
  margin-bottom: 5px;
}

.barcode-wrapper {
  background: #fafafa;
  border: 1px dashed #ddd;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}
.final-wrapper {
  background: #fafafa;
  padding: 20px;
  overflow: hidden;
}
.final-wrapper p {
  line-height: 1;
  margin: 10px 0;
  font-weight: 300;
  display: flex;
  justify-content: center;
  align-items: center;
}
.final-wrapper .text {
  font-size: 10px;
  color: #333;
}
.final-wrapper .company {
  font-size: 11px;
  color: #444;
}
</style>
<style>
@media print {
  @page {
    size: 4in 6in; /* Width Height */
    margin: 0;     /* Removes browser default headers/footers */
  }

  /* Hides all children of body */
  body * {
    visibility: hidden;
  }

  /* 2. Explicitly hide common admin layout elements using display:none
     (just in case they have higher specificity) */
  nav.header-navbar,
  .main-menu,
  .header-navbar-shadow,
  .content-header,
  footer.footer,
  .customizer-toggle,
  .scroll-to-top {
    display: none !important;
  }

  /* 3. Make the Label Visible and Position it */
  #printable-section, #printable-section * {
    visibility: visible !important;
  }

  #printable-section {
    visibility: visible !important;
    position: fixed !important; /* Fixed helps lock it to the page corner */
    left: 0 !important;
    top: 0 !important;
    background-color: white !important;

    /* Remove borders for actual printing usually, keeps it clean */
    border: none !important;
    box-shadow: none !important;
    box-sizing: border-box !important;
  }

  /* 4. Force Print Colors */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
